import {inject, Injectable} from "@angular/core";
import { Auth } from 'aws-amplify';
import {AuthService} from "../../services/auth.service";
import { Router, ActivatedRoute, ActivatedRouteSnapshot, RouterStateSnapshot, CanActivate } from '@angular/router';
import {AuthenticatorService} from "@aws-amplify/ui-angular";
import {MatSnackBar} from "@angular/material/snack-bar";
import {AWSLambda} from "../../AWSLambda.service";
import { Observable } from 'rxjs';
import { map, take, filter, switchMap } from 'rxjs/operators';

@Injectable({ providedIn: 'root' })
export class BoRolesCheckService implements CanActivate {
  snackBar = inject(MatSnackBar);
  lambda = inject(AWSLambda);
  private user = inject(AuthenticatorService).user;
  private auth = inject(AuthService);

  private adminUsersRoutes : string[] = [
    '/backoffice/users',
    '/backoffice/users/:userId',
    '/backoffice/users/edit/:userId',
  ];
  private adminPromosRoutes : string[] = [
    '/backoffice/promotionCodes',
    '/backoffice/promotionCodes/edit',
    '/backoffice/promotionCodes/edit/:id',
  ];
  private adminTransactionsRoutes : string[] = [
    '/backoffice/transactions',
    '/backoffice/txusuarios',
  ];
  private logErrorRoutes : string[] = [
    '/backoffice/errors',
  ];
  private superAdminRoutes : string[] = [
    '/backoffice/calls-pricing',
    '/backoffice/calls-pricing/edit',
    '/backoffice/calls-pricing/edit/:id',
    '/backoffice/configs',
    '/backoffice/configs/edit/:id',
    '/backoffice/excelConverter',
    '/backoffice/partners',
    '/backoffice/partners/:userId',
    '/backoffice/conditions/:type',
    '/backoffice/users/esims/:userId',
  ];

  constructor(
    private authService: AuthService,
    private router: Router
  ) { }

  /**
   * Angular Route Guard - ejecuta ANTES de cargar el componente
   */
  canActivate(
    route: ActivatedRouteSnapshot,
    state: RouterStateSnapshot
  ): Observable<boolean> | Promise<boolean> | boolean {
    // Esperar a que AuthService termine de inicializar (evitar race condition)
    return this.auth.initialized$.pipe(
      filter(initialized => initialized === true),
      take(1),
      switchMap(async () => {
        return await this.checkAccessForUrl(state.url);
      }),
      map(result => result)
    ).toPromise() as Promise<boolean>;
  }

  /**
   * Método manual - para compatibilidad con componentes existentes (DEPRECATED)
   * @deprecated Use CanActivate guard on routes instead
   */
  async checkAccess() {
    const url = this.router.url;
    return await this.checkAccessForUrl(url);
  }

  /**
   * Lógica central de verificación de acceso
   */
  private async checkAccessForUrl(url: string): Promise<boolean> {
    try {
      await this.auth.refresh();

      // Intentar obtener datos del usuario desde Lambda
      let userData;
      try {
        userData = await this.lambda.getLambdaFunction("lambda", "/get/userDetails");
        // console.log('>> bo rolesCheck userData:', userData);
      } catch (lambdaError) {
        console.error('BoRolesCheck: Error al obtener userDetails desde Lambda:', lambdaError);

        // DECISIÓN DE SEGURIDAD: Bloquear acceso completamente
        // NO usar fallback a localStorage por seguridad
        this.snackBar.open('Error de conexión al verificar permisos. Recarga la página.', 'Close', {
          duration: 5000,
        });
        return false;  // Bloquear acceso - no redirigir
      }

      // console.log('>> bo rolesCheck route:', url);

      // Orden de comprobación:
      // - usuario está habilitado
      // - es superadmin
      // - intenta acceder a rutas con acceso sólo para superadmin
      // - tiene acceso al BO
      // - tiene acceso a la sección específica del BO

      if (!this.isEnabled(userData)) {
        this.redirectToHome();
        return false;
      }

      if (this.isSuperAdmin(userData)) {
        return true;
      }

      if (this.matchesRoutePattern(url, this.superAdminRoutes)) {
        this.redirectToDashboard();
        return false;
      }

      if (!this.canAccessBackOffice(userData)) {
        this.redirectToHome();
        return false;
      }

      if (this.matchesRoutePattern(url, this.adminUsersRoutes) && !this.canAdminUsers(userData)) {
        this.redirectToDashboard();
        return false;
      }

      if (this.matchesRoutePattern(url, this.adminPromosRoutes) && !this.canAdminPromotions(userData)) {
        this.redirectToDashboard();
        return false;
      }

      if (this.matchesRoutePattern(url, this.adminTransactionsRoutes) && !this.canAdminTransactions(userData)) {
        this.redirectToDashboard();
        return false;
      }

      if (this.matchesRoutePattern(url, this.logErrorRoutes) && !this.canAdminLogErrors(userData)) {
        this.redirectToDashboard();
        return false;
      }

      return true;
    } catch (error) {
      console.error('BoRolesCheck: Error inesperado en checkAccess:', error);
      // NO redirigir automáticamente - solo bloquear acceso
      this.snackBar.open('Error al verificar permisos. Intenta recargar la página.', 'Close', {
        duration: 5000,
      });
      return false;
    }
  }

  private isEnabled(userData: any) {
    return userData.Enabled === true;
  }

  private isSuperAdmin(userData: any) {
    return userData['custom:backofficeSuperAdmin'] === '1';
  }

  private canAccessBackOffice(userData: any) {
    return userData['custom:Portal'].includes('backoffice');
  }

  private canAdminTransactions(userData: any) {
    return userData['custom:backofficeRoles']?.includes('adminTransactions');
  }

  private canAdminUsers(userData: any) {
    return userData['custom:backofficeRoles']?.includes('adminUsers');
  }

  private canAdminPromotions(userData: any) {
    return userData['custom:backofficeRoles']?.includes('adminPromotions');
  }

  private canAdminLogErrors(userData: any) {
    return userData['custom:backofficeRoles']?.includes('adminLogErrors');
  }

  private redirectToDashboard() {
    this.snackBar.open('Usuario no autorizado para gestionar esta sección.', 'Close', {
      duration: 5000,
    });
    this.router.navigate(['/backoffice/dashboard'], { replaceUrl: true });
  }

  private redirectToHome() {
    console.warn('BoRolesCheck: Usuario no tiene acceso a backoffice, redirigiendo...');

    // Verificar si el usuario tiene portal 'cliente'
    const userPortal = localStorage.getItem('userPortal') || '';
    if (userPortal.includes('cliente')) {
      this.router.navigate(['/home'], { replaceUrl: true });
    } else {
      // Usuario no tiene ningún portal válido
      this.snackBar.open('No tienes acceso a ningún portal.', 'Close', {
        duration: 5000,
      });
      this.router.navigate(['/onboarding/login'], { replaceUrl: true });
    }
  }

  private matchesRoutePattern(url: string, routePatterns: string[]): boolean {
    return routePatterns.some(pattern => {
      if (pattern.includes(':')) {
        const regexPattern = pattern
          .replace(/:[^\/]+/g, '[^/]+')
          .replace(/\//g, '\\/');
        const regex = new RegExp(`^${regexPattern}$`);
        return regex.test(url);
      } else {
        return url === pattern;
      }
    });
  }
}
